
import React, { useState } from 'react';
import { User, Bot, MoreHorizontal, Trash2, Pencil, RefreshCw, Zap, RotateCcw, Layers } from 'lucide-react';
import { SessionInput } from '@/types/api';
import { useSessionStore } from '@/store/sessionStore';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { revertAiTurn } from '@/lib/api/action';
import { regenerateAiResponse as apiRegenerateAiResponse } from '@/lib/api/session';
import RawLlmResponseDialog from './RawLlmResponseDialog';
import { truncateText, formatCollapsedActionPreview } from '@/utils/groupSessionInputs';

interface CollapsedMessageItemProps {
    input: SessionInput;
    type: 'user' | 'ai';
    isActive: boolean;
    onClick: () => void;
    showCheckbox?: boolean;
    isSelected?: boolean;
    onToggleSelection?: () => void;
    onSelectRange?: () => void;  // For shift-click range selection
}

const CollapsedMessageItem: React.FC<CollapsedMessageItemProps> = ({
    input,
    type,
    isActive,
    onClick,
    showCheckbox = false,
    isSelected = false,
    onToggleSelection,
    onSelectRange,
}) => {
    const {
        latestSessionInput,
        setActivePlanInput,
        deleteSessionInput,
        deleteSessionInputUntilHere,
        currentSessionId,
        currentSessionDetails,
        regenerateAiResponse,
        startRetryMode,
        lastSelectedInputId,
        selectRange,
        setAutoScrollEnabled,
    } = useSessionStore();

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isRegenerateDialogOpen, setIsRegenerateDialogOpen] = useState(false);
    const [isRawResponseDialogOpen, setIsRawResponseDialogOpen] = useState(false);

    const safeAiActions = input.aiActions || [];
    const inputs = currentSessionDetails?.sessionInputs || [];
    const isLastInput = inputs.length > 0 && inputs[inputs.length - 1].id === input.id;
    const inputsAfterIndex = inputs.findIndex(i => i.id === input.id) + 1;
    const inputsToDeleteCount = inputs.length - inputsAfterIndex;

    // Token info for this specific message
    const inputTokens = input.input_token_count || 0;
    const outputTokens = input.output_token_count || 0;
    const cachedTokens = input.cached_token_count || 0;
    const hasTokenInfo = inputTokens > 0 || outputTokens > 0;

    // For AI: check if can revert/regenerate
    const canRevert = type === 'ai' && !!input.raw_studio_request_body;
    
    // For User: check if can retry
    const isLatestUserInput = type === 'user' && isLastInput && (input.user_prompt || '').trim() !== '';

    // Discarded state
    const isDiscarded = input.is_discarded === true;

    // Summary state
    const isSummary = input.is_summary === true;
    const isSummarized = !!input.replaced_by_summary_input_id && isDiscarded;

    // Build preview content
    const getPreviewContent = () => {
        if (type === 'user') {
            return truncateText(input.user_prompt || 'No prompt', 100);
        }
        
        // AI message - show tool calls
        if (safeAiActions.length > 0) {
            return safeAiActions.map(action => {
                const toolName = action.action_type;
                const argPreview = formatCollapsedActionPreview(action);
                if (argPreview) {
                    return `${toolName}: ${argPreview}`;
                }
                return toolName;
            }).join(' • ');
        }
        
        if (input.error_message) {
            return `Error: ${truncateText(input.error_message, 50)}`;
        }
        
        if (input.llm_response_explanation) {
            return truncateText(input.llm_response_explanation, 100);
        }
        
        if (input.raw_llm_response) {
            return truncateText(input.raw_llm_response, 100);
        }
        
        return 'Processing...';
    };

    // Handlers
    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onClick();
        setAutoScrollEnabled(false);
    };

    const handleDelete = async () => {
        if (!currentSessionId) {
            toast.error('No active session');
            return;
        }
        try {
            if (isLastInput) {
                await deleteSessionInput(currentSessionId, input.id);
                toast.success('Turn deleted successfully');
            } else {
                await deleteSessionInputUntilHere(currentSessionId, input.id);
                toast.success(`${inputsToDeleteCount} turn(s) deleted successfully`);
            }
            setIsDeleteDialogOpen(false);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to delete turn';
            toast.error(errorMessage);
        }
    };

    const handleRetry = () => {
        if (input.user_prompt) {
            startRetryMode(input.id, input.user_prompt);
            toast.info('Edit the prompt and click Retry to resubmit');
        }
    };

    const handleRevert = async () => {
        try {
            toast.info('Sending revert command...');
            await revertAiTurn(input.id);
        } catch (error) {
            toast.error('Failed to send revert command.');
        }
    };

    const handleRegenerate = async () => {
        if (!currentSessionId) {
            toast.error('No active session');
            return;
        }
        try {
            setIsRegenerateDialogOpen(false);
            await regenerateAiResponse(input.id);
            toast.success('Regeneration started...');
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to regenerate';
            toast.error(errorMessage);
        }
    };

    // Determine styling based on type and state
    const bgClass = isSummarized
        ? 'bg-amber-50 dark:bg-amber-950/20 opacity-50'
        : isDiscarded
            ? 'bg-slate-100 dark:bg-slate-800/50 opacity-60'
            : isSummary
                ? 'bg-amber-50 dark:bg-amber-950/30'
                : type === 'user'
                    ? 'bg-indigo-50 dark:bg-indigo-950/30'
                    : input.error_message
                        ? 'bg-red-50 dark:bg-red-900/20'
                        : 'bg-emerald-50 dark:bg-emerald-900/20';

    const iconClass = isSummary
        ? 'bg-amber-500'
        : type === 'user'
            ? 'bg-indigo-500'
            : input.error_message
                ? 'bg-red-500'
                : 'bg-emerald-500';

    const Icon = isSummary ? Layers : type === 'user' ? User : Bot;

    const handleCheckboxClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (e.shiftKey && lastSelectedInputId) {
            // Shift-click: select range from last selected to current
            selectRange(lastSelectedInputId, input.id);
        } else {
            // Normal click: toggle single selection
            onToggleSelection?.();
        }
    };

    return (
        <>
            <ContextMenu>
                <ContextMenuTrigger asChild>
                    <div
                        onClick={handleClick}
                        className={cn(
                            'group flex items-center gap-2 px-2 py-1 rounded cursor-pointer transition-colors',
                            bgClass,
                            'hover:ring-1 hover:ring-slate-300 dark:hover:ring-slate-600',
                            isActive && !isDiscarded && 'ring-2 ring-indigo-500 dark:ring-indigo-400 bg-indigo-100 dark:bg-indigo-950/50',
                            isSelected && 'ring-2 ring-blue-500 dark:ring-blue-400'
                        )}
                    >
                        {/* Checkbox (when in selection mode) */}
                        {showCheckbox && (
                            <div
                                onClick={handleCheckboxClick}
                                className={cn(
                                    'w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center cursor-pointer',
                                    isSelected
                                        ? 'bg-blue-500 border-blue-500 text-white'
                                        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700'
                                )}
                            >
                                {isSelected && (
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                )}
                            </div>
                        )}

                        {/* Icon */}
                        <div className={cn('rounded-full p-0.5 text-white flex-shrink-0', iconClass)}>
                            <Icon className="h-3 w-3" />
                        </div>

                        {/* Preview content */}
                        <span className="text-xs text-slate-700 dark:text-slate-300 truncate flex-1 font-mono">
                            {getPreviewContent()}
                        </span>

                        {/* Summarized badge */}
                        {isSummarized && (
                            <span className="text-[10px] bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded font-medium uppercase">
                                Summarized
                            </span>
                        )}

                        {/* Summary badge */}
                        {isSummary && !isDiscarded && (
                            <span className="text-[10px] bg-amber-200 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200 px-1.5 py-0.5 rounded font-medium uppercase">
                                Summary
                            </span>
                        )}

                        {/* Discarded badge (non-summarized) */}
                        {isDiscarded && !isSummarized && (
                            <span className="text-[10px] bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-1.5 py-0.5 rounded font-medium uppercase">
                                Discarded
                            </span>
                        )}

                        {/* Token badge - always visible */}
                        {hasTokenInfo && !isDiscarded && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className="flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400 font-mono bg-emerald-100 dark:bg-emerald-900/30 px-1 py-0.5 rounded">
                                            <Zap className="h-2 w-2" />
                                            <span>{(inputTokens + outputTokens).toLocaleString()}</span>
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="text-[10px]">
                                        <div className="space-y-0.5">
                                            <div>In: {inputTokens.toLocaleString()}</div>
                                            <div>Out: {outputTokens.toLocaleString()}</div>
                                            {cachedTokens > 0 && (
                                                <div>Cache: {cachedTokens.toLocaleString()}</div>
                                            )}
                                        </div>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </div>
                </ContextMenuTrigger>

                <ContextMenuContent>
                    {/* Summary inputs: no destructive context menu actions */}
                    {isSummary && (
                        <ContextMenuItem disabled>
                            <Layers className="h-3 w-3 mr-2" />
                            Summary — use batch discard to remove from context
                        </ContextMenuItem>
                    )}

                    {/* User context menu (not for summaries) */}
                    {type === 'user' && !isSummary && (
                        <>
                            <ContextMenuItem onClick={handleRetry} disabled={!isLatestUserInput}>
                                <Pencil className="h-3 w-3 mr-2" />
                                Edit & Retry
                            </ContextMenuItem>
                            <ContextMenuItem 
                                onClick={() => setIsDeleteDialogOpen(true)}
                                className="text-red-600 focus:text-red-600"
                            >
                                <Trash2 className="h-3 w-3 mr-2" />
                                {isLastInput ? 'Delete Turn' : `Delete Until Here (${inputsToDeleteCount} more)`}
                            </ContextMenuItem>
                        </>
                    )}

                    {/* AI context menu */}
                    {type === 'ai' && (
                        <>
                            <ContextMenuItem onClick={handleRevert} disabled={!canRevert}>
                                <RotateCcw className="h-3 w-3 mr-2" />
                                Revert
                            </ContextMenuItem>
                            <ContextMenuItem onClick={() => setIsRegenerateDialogOpen(true)}>
                                <RefreshCw className="h-3 w-3 mr-2" />
                                Regenerate
                            </ContextMenuItem>
                            <ContextMenuItem onClick={() => setIsRawResponseDialogOpen(true)}>
                                View Raw Response
                            </ContextMenuItem>
                            <ContextMenuItem 
                                onClick={() => setIsDeleteDialogOpen(true)}
                                className="text-red-600 focus:text-red-600"
                            >
                                <Trash2 className="h-3 w-3 mr-2" />
                                {isLastInput ? 'Delete Turn' : `Delete Until Here (${inputsToDeleteCount} more)`}
                            </ContextMenuItem>
                        </>
                    )}
                </ContextMenuContent>
            </ContextMenu>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {isLastInput ? 'Delete this turn?' : `Delete ${inputsToDeleteCount} turn(s)?`}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {isLastInput 
                                ? 'This will permanently delete this turn. This cannot be undone.'
                                : `This will delete this turn and all ${inputsToDeleteCount} turn(s) after it. This cannot be undone.`
                            }
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Regenerate Confirmation Dialog */}
            <AlertDialog open={isRegenerateDialogOpen} onOpenChange={setIsRegenerateDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {isLastInput ? 'Regenerate AI response?' : `Regenerate and delete ${inputsToDeleteCount} turn(s)?`}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {isLastInput
                                ? 'This will regenerate the AI response. The existing response will be replaced.'
                                : `This will regenerate the AI response and delete all ${inputsToDeleteCount} turn(s) after it.`
                            }
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleRegenerate}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Regenerate
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Raw LLM Response Dialog */}
            <RawLlmResponseDialog
                input={input}
                open={isRawResponseDialogOpen}
                onOpenChange={setIsRawResponseDialogOpen}
            />
        </>
    );
};

export default CollapsedMessageItem;